import { Form, Formik } from "formik";
import SkeletonBase from "newui/components/Skeleton";
import DocumentationComp from "pages/RFQSubmission/RFQPreview/DocumentationComp";
import FormErrorComp from "pages/RFQSubmission/RFQPreview/FormErrorComp";
import { cn, isEmptyObj } from "utils/helpers";
import ReturnableSchedule from "../ReturnableSchedule";
import { RFX_TABS } from "utils/constants";
import QuoteFile from "pages/RFQSubmission/RFQPreview/QuoteFile";
import { QuoteTab } from "../QuoteTab";
import type { QuoteTabHandle } from "../QuoteTab/BidderQuoteTab";
import { AddendumContainer } from "pages/RFQSubmission/addendum/AddendumContainer";
import { PreviewOnlyComp } from "components/RFQ/PreviewOnlyComp/PreviewOnlyComp";
import {
	canEditRfxResponse,
	canViewCurrentTab,
	hasInviteeSubmittedRfx,
} from "helpers/rfxHelper";
import { allValidationSchema } from "pages/RFQSubmission/RFQPreview/RFQUtils/ValidationSchemas";
import { hasRfxSubmissionDeadlinePassed } from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import { isRFxClosed } from "newui/features/Rfx/utils";
import { SubmissionDisplay } from "../../types/submission.type";
import { RfxTab } from "newui/features/Rfx/types/component-types";
import { FlexibleQuoteFile } from "pages/RFQSubmission/RFQPreview/FlexibleQuoteFile";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import QuoteInstructions from "./QuoteInstructions";
import CommunicationsPanel from "newui/features/Conversation";
import { ProcuraconContextType } from "newui/types/ContextType.type";
import { useEffect, useRef, useState } from "react";
import { fetchConversationsByContextId } from "newui/features/Conversation/api";
import { useParams } from "@reach/router";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { Conversation } from "newui/features/Conversation/types/Conversation.type";
import { ClarificationsRegister } from "newui/features/Rfx/Clarifications";
import { EvaluationTabDisplay } from "newui/features/Rfx/Evaluation";
import { useHasEvaluationAccess } from "hooks/featureFlags";
import { useIsMenuCollapsed } from "newui/hooks/useMenuStatus";

interface ViewSubmissionProps {
	currentRevision?: number;
	currentTab?: string;
	documentFilter?: any;
	errors?: any;
	formRef?: any;
	guestEmail?: string;
	hasDocuments?: boolean;
	initialFormValue?: any;
	isClientView: boolean;
	isGuest?: boolean;
	isLoading?: boolean;
	isReadOnlyMode?: boolean;
	quoteTabRef?: React.RefObject<QuoteTabHandle>;
	rfxInviteId?: number;
	rfxInviteStatus?: string;
	submissionData: SubmissionDisplay;
	submissionStatus?: string; // rfxSubmission.status
	validForm?: boolean;
	setErrors: (e: any) => void;
	updateTabCount: (tabId: RfxTab, count: number) => void;
	onDraftRevisionChange?: (hasDraft: boolean) => void;
	onQuoteRevisionUpdated?: (isQuoteTabValid?: boolean) => void;
	setValidForm?: (v: boolean) => void;
}

export const ViewSubmission: React.FC<ViewSubmissionProps> = ({
	currentTab,
	documentFilter,
	errors,
	formRef,
	guestEmail,
	hasDocuments,
	initialFormValue,
	isClientView,
	isGuest,
	isLoading,
	isReadOnlyMode,
	submissionData,
	submissionStatus,
	rfxInviteStatus,
	onDraftRevisionChange,
	onQuoteRevisionUpdated,
	quoteTabRef,
	validForm,
	setErrors,
	setValidForm,
}) => {
	const { rfxData, rfxSubmissionId, rfxSubmissionData, rfxId, rfxTabTotals, setRfxTabTotals } =
		useRfxSubmissionData();
	const canEditRfx =
		!isClientView && canEditRfxResponse(isLoading, rfxData, submissionStatus);
	const rfxHasQuote = rfxData?.rfxType?.show_quote;
	const isQuoteTabValid = rfxSubmissionData?.isQuoteTabValid ?? true;
	const needsQuoteValidation = rfxHasQuote && rfxData?.quoteType?.toLowerCase() !== "file";
	const quoteTabInvalid = needsQuoteValidation && !isQuoteTabValid;
	const hasDeadlinePassed = hasRfxSubmissionDeadlinePassed(rfxData);
	const rfxIsClosed = isRFxClosed(rfxData?.status ?? "");
	const hasSubmitted = hasInviteeSubmittedRfx(submissionStatus);
	const canAskQuestions = hasDeadlinePassed || hasSubmitted || rfxIsClosed;
	const canViewTab = canViewCurrentTab(currentTab);
	const { guestPaths, supplierPaths } = useAuthContext();

	const isSideBarCollapsed = useIsMenuCollapsed();
	const hasEvaluationAccess = useHasEvaluationAccess();

	const [{ conversations, initialConversation }, setConversations] = useState({
		conversations: [] as Conversation[],
		initialConversation: null as Conversation,
	});
	const { contextId } = useParams<{ contextId: string }>();
	const [areCommsLoading, setAreConversationsLoading] = useState(true);

	/**
	 * Loads all conversations for this RFx
	 */
	useEffect(() => {
		if (!rfxSubmissionId || currentTab !== RFX_TABS.COMMS) {
			return;
		}

		setAreConversationsLoading(true);

		fetchConversationsByContextId(
			rfxSubmissionId,
			ProcuraconContextType.RFX_PRE_SUBMISSION
		)
			.then((conversations) => {
				const initialConversation =
					conversations.find((c) => c.id.toString() === contextId) || null;
				setConversations({ conversations, initialConversation });
			})
			.finally(() => setAreConversationsLoading(false));
	}, [rfxSubmissionId, contextId, currentTab]);

	const onConversationCreated = (conversation: Conversation): void => {
		const newConversations = [conversation, ...conversations];
		setRfxTabTotals({ ...rfxTabTotals, comms: rfxTabTotals.comms + 1 });
		setConversations({
			conversations: newConversations,
			initialConversation: conversation,
		});
	};

	const onConversationSelected = (conversation) => {
		const path =
			supplierPaths?.viewRfxSubmission(
				rfxId,
				rfxSubmissionId,
				RFX_TABS.COMMS,
				conversation.id
			) ??
			guestPaths?.viewRfxSubmission(rfxId, RFX_TABS.COMMS, conversation.id);
		window.history.replaceState({}, "", path);
	};

	const onConversationUpdated = (updatedConversation: Conversation): void => {
		const updatedConversations = conversations.map((conversation) => conversation.id === updatedConversation.id ? updatedConversation : conversation);

		setConversations({
			conversations: updatedConversations,
			initialConversation: initialConversation,
		});
	};

	if (isLoading || !rfxData || !rfxSubmissionData) {
		return (
			<>
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="72px"
					className="my-4 !rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="72px"
					className="!rounded-lg"
				/>
			</>
		);
	}

	const handleSubmit = (
		values: any,
		formikHelpers: any
	): void | Promise<any> => {
		return;
	};

	const quotes = submissionData?.quotes;
	const returnableSchedule = submissionData?.returnableSchedules;

	return (
		// This id rfx-container is used as a link target
		<div
			className="relative"
			id="rfx-container"
		>
			{((errors && !validForm) || quoteTabInvalid) && (
				<FormErrorComp
					isReturnableError={
						errors?.question_answer ||
						errors?.returnable_schedule_file_responses
					}
					isQuoteError={errors?.quote_answer || errors?.quote_file || quoteTabInvalid}
				/>
			)}
			<div className="relative">
				{currentTab === RFX_TABS.DOCUMENTATION && (
					<DocumentationComp
						data={rfxData?.documentationDocument}
						documentFilter={documentFilter}
					/>
				)}

				{initialFormValue && (
					<Formik
						key={initialFormValue ? submissionData?.rfx?.id : "loading"}
						initialValues={initialFormValue}
						innerRef={formRef}
						initialErrors={errors}
						validationSchema={
							!isReadOnlyMode &&
							allValidationSchema(
								returnableSchedule,
								quotes?.questions,
								isGuest,
								hasDocuments,
								submissionData?.hasPastProjects,
								submissionData?.hasKeyPersonnel
							)
						}
						validateOnBlur
						validateOnMount={false}
						onSubmit={handleSubmit}
					>
						{({ isValid, touched, errors, values }) => {
							if (!isEmptyObj(touched)) {
								setValidForm((canEditRfx && isValid) || !canEditRfx);
								setErrors(errors);
							}

							return (
								<Form>
									{currentTab === RFX_TABS.QUESTIONNAIRE && !isLoading && (
										<ReturnableSchedule
											hasKeyPersonnel={submissionData?.hasKeyPersonnel}
											hasPastProjects={submissionData?.hasPastProjects}
											rfxInviteId={submissionData?.rfxInviteId}
											returnableSchedule={returnableSchedule?.questions}
											rfxAnswers={returnableSchedule?.answers}
											isReturnableSchedulesTypeFile={returnableSchedule?.type}
											companyName={rfxData?.companyOwner?.name}
											templateReturnableScheduleFile={
												returnableSchedule?.templateFile
											}
											disabled={isReadOnlyMode}
										/>
									)}

									{currentTab === RFX_TABS.QUOTE && (
										<>
											<div className="mb-4">
												<QuoteInstructions
													isLoading={isLoading}
													quoteInstructions={rfxData?.quoteInstructions}
												/>
											</div>
											{quotes?.type === "file" ? (
												<QuoteFile
													companyName={rfxData?.companyOwner?.name}
													quoteFilepath={rfxData?.quoteFile?.file_path}
													disabled={isReadOnlyMode}
													rfxId={rfxData.id}
													rfxInviteId={submissionData?.rfxInviteId}
													submissionQuoteFiles={
														submissionData.quotes.responseFiles
													}
												/>
											) : (
												<QuoteTab
													ref={quoteTabRef}
													isClientView={isClientView}
													listQuotes={quotes?.questions}
													disabled={isReadOnlyMode}
													inviteStatus={rfxInviteStatus ?? submissionStatus ?? ""}
													onDraftRevisionChange={onDraftRevisionChange}
													onQuoteRevisionUpdated={onQuoteRevisionUpdated}
													rfxId={rfxId}
													rfxInviteId={rfxSubmissionData.id}
												/>
											)}
											{rfxData?.allow_flexible_quote && (
												<FlexibleQuoteFile
													rfqId={rfxData?.id}
													rfxInviteId={submissionData?.rfxInviteId}
													clientName={rfxData?.client}
													flexibleQuoteSavedFiles={
														quotes?.flexibleResponseFiles
													}
													disabled={isReadOnlyMode}
												/>
											)}
										</>
									)}
								</Form>
							);
						}}
					</Formik>
				)}
				{currentTab === RFX_TABS.COMMS && (
					<CommunicationsPanel
						canCreateConversation={!isClientView}
						additionalContextId={rfxId}
						contextId={rfxSubmissionId}
						contextType={ProcuraconContextType.RFX_PRE_SUBMISSION}
						isLoading={areCommsLoading}
						initialConversation={initialConversation}
						conversations={conversations}
						onConversationCreated={onConversationCreated}
						onConversationSelected={onConversationSelected}
						onConversationUpdated={onConversationUpdated}
					/>
				)}
				{currentTab === RFX_TABS.ADDENDUMS && (
					<AddendumContainer
						rfqId={rfxData.id}
						isGuest={isGuest}
						guestEmail={guestEmail}
						disabled={canAskQuestions}
						isCommunication={currentTab === RFX_TABS.COMMS}
					/>
				)}
				{currentTab === RFX_TABS.CLARIFICATIONS && (
					<ClarificationsRegister
						rfxSubmissionId={rfxSubmissionId}
						isClientView={isClientView}
						supplierName={rfxSubmissionData?.company?.name}
					/>
				)}
				{currentTab === RFX_TABS.EVALUATION && isClientView && hasEvaluationAccess && (
					<EvaluationTabDisplay
						rfxData={rfxData}
						rfxSubmissionId={rfxSubmissionId}
						isClientView={isClientView}
					/>
				)}
			</div>
			{!isLoading && !canEditRfx && !canViewTab && !isClientView && (
				<>
					<div
						className={`pointer-events-none absolute inset-0 z-50 pt-2 ${!hasSubmitted && "bg-[rgba(255,255,255,0.66)]"}`}
					>
						<div
							className={cn(
								"pointer-events-auto sticky top-0 mx-auto pt-2 sm:w-max",
								!isSideBarCollapsed
									? "sm:w-[calc(100%-70px)] md:w-[calc(100%-70px)] lg:w-max"
									: ""
							)}
						>
							<PreviewOnlyComp
								icon={
									(!hasSubmitted && "alert-triangle") ||
									(hasDeadlinePassed && hasSubmitted && "check") ||
									null
								}
								isRFxClosed={rfxIsClosed}
								hasDeadlinePassed={hasDeadlinePassed}
								hasSubmitted={hasSubmitted}
								label={
									// display specific messages based on the state
									// 1. deadline not passed and have submitted
									// 2. deadline passed and have submitted
									// 3. deadline passed and not submitted
									// 4. default message
									(!hasDeadlinePassed &&
										hasSubmitted &&
										"Bid submitted. To make amendments, please click the 'Edit Response' button.") ||
									(hasDeadlinePassed &&
										hasSubmitted &&
										"Submission deadline has passed, your submission was made successfully.") ||
									(hasDeadlinePassed &&
										!hasSubmitted &&
										"Deadline for submission has now passed, you cannot make a submission.") ||
									"Preview only"
								}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
};
